import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { useEffect, useState } from 'react';
import { Link, useParams } from 'react-router-dom';
import { useShallow } from 'zustand/react/shallow';

import { DetailPageLayout } from '../../../components/layouts';
import { usePageTitle } from '../../../hooks/shared';
import { scrapersAPI } from '../api/scrapers';
import { useDiscoveryStore } from '../store/discoveryStore';
import { useScraperProgressStore } from '../store/scraperProgressStore';

const STATUS_STYLES = {
  completed: 'text-emerald-600',
  running: 'text-blue-600',
  error: 'text-red-600',
};
const FETCH_STRATEGY_STYLES = {
  http: 'bg-emerald-50 text-emerald-700 border-emerald-200',
  browser: 'bg-amber-50 text-amber-700 border-amber-200',
  stealth_browser: 'bg-orange-50 text-orange-700 border-orange-200',
  fallback: 'bg-yellow-50 text-yellow-700 border-yellow-200',
  failed: 'bg-red-50 text-red-700 border-red-200',
};

function fmt(ts) {
  if (!ts) return '—';
  return new Date(ts).toLocaleString();
}

function ms(n) {
  if (n === null || n === undefined) return '—';
  return n < 1000 ? `${n}ms` : `${(n / 1000).toFixed(1)}s`;
}

function elapsed(startTime) {
  if (!startTime) return '—';
  const s = Math.floor((Date.now() - startTime) / 1000);
  if (s < 60) return `${s}s`;
  const m = Math.floor(s / 60);
  return `${m}m ${s % 60}s`;
}

/**
 * Build a tree structure from flat URLs
 * e.g., ['https://example.com/foo', 'https://example.com/foo/bar']
 * becomes nested nodes where each URL is a child of its parent directory
 */
function buildUrlTree(urls) {
  const tree = {};

  urls.forEach((url) => {
    try {
      const u = new URL(url);
      const pathname = u.pathname;
      const parts = pathname.split('/').filter(Boolean);

      let node = tree;
      for (let i = 0; i < parts.length; i++) {
        const part = parts[i];
        if (!node[part]) node[part] = {};
        node = node[part];
      }
      node._url = url;
    } catch {
      // Invalid URL, skip
    }
  });

  return tree;
}

/**
 * Render a tree node recursively
 */
function TreeNode({ name, node, depth = 0 }) {
  const isLeaf = !Object.keys(node).some((k) => k !== '_url');
  const indent = depth * 20;

  if (isLeaf && node._url) {
    return (
      <div key={`${name}-${depth}`} style={{ marginLeft: `${indent}px` }} className="text-xs">
        <a
          href={node._url}
          target="_blank"
          rel="noopener noreferrer"
          className="text-primary-600 hover:text-primary-700 hover:underline"
        >
          {name}
        </a>
      </div>
    );
  }

  return (
    <div key={`${name}-${depth}`} style={{ marginLeft: `${indent}px` }}>
      <div className="text-xs font-medium text-neutral-700">📁 {name}</div>
      {Object.entries(node)
        .filter(([k]) => k !== '_url')
        .map(([childName, childNode]) => (
          <TreeNode key={childName} name={childName} node={childNode} depth={depth + 1} />
        ))}
    </div>
  );
}

/**
 * Render the full URL tree
 */
function UrlTree({ urls, baseUrl }) {
  if (urls.length === 0) {
    return <p className="text-xs text-neutral-500">No URLs crawled yet.</p>;
  }

  const tree = buildUrlTree(urls);
  const origin = baseUrl ? new URL(baseUrl).hostname : 'domain';

  return (
    <div className="font-mono text-xs space-y-1 max-h-96 overflow-y-auto">
      <div className="font-medium text-neutral-700">🌐 {origin}</div>
      {Object.entries(tree)
        .filter(([k]) => k !== '_url')
        .map(([name, node]) => (
          <TreeNode key={name} name={name} node={node} depth={1} />
        ))}
    </div>
  );
}

export default function AdminScraperDetail() {
  const { state } = useParams();
  const code = state.toUpperCase();
  const queryClient = useQueryClient();
  usePageTitle(`${code} Scraper`);

  // Discovery store — useShallow prevents infinite loops from selector returning new objects
  const { liveStats, crawledUrls, isDiscoveryRunning, startDiscovery, stopDiscovery } =
    useDiscoveryStore(
      useShallow((s) => ({
        liveStats: s.liveStats[code] ?? null,
        crawledUrls: s.crawledUrls[code] ?? null,
        isDiscoveryRunning: Boolean(s.activeRequests[code]),
        startDiscovery: s.startDiscovery,
        stopDiscovery: s.stopDiscovery,
      }))
    );

  const { data: scrapersData } = useQuery({
    queryKey: ['scrapers-states'],
    queryFn: scrapersAPI.listStateScrapers,
  });

  const {
    data: sessionsData,
    isLoading,
    error,
  } = useQuery({
    queryKey: ['scraper-sessions', code],
    queryFn: () => scrapersAPI.getDiscoverySessions(code),
    enabled: !!code,
  });

  const { data: electionsData, isLoading: electionsLoading } = useQuery({
    queryKey: ['elections-by-datasource', `state_${code.toLowerCase()}`],
    queryFn: () => scrapersAPI.getElectionsByDataSource(`state_${code.toLowerCase()}`),
    enabled: !!code,
  });

  const { data: urlsData } = useQuery({
    queryKey: ['scraper-authority-urls', code],
    queryFn: () => scrapersAPI.getStateAuthorityUrls(code),
    enabled: !!code,
  });

  const [urlEdit, setUrlEdit] = useState(null); // null = view mode, object = edit mode
  const [cacheMode, setCacheMode] = useState('write');
  const [verbose, setVerbose] = useState(false);

  const urlMutation = useMutation({
    mutationFn: (data) => scrapersAPI.updateStateAuthorityUrls(code, data),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['scraper-authority-urls', code] });
      setUrlEdit(null);
    },
  });

  const scraper = scrapersData?.scrapers?.find((s) => s.state === code);
  const sessions = sessionsData?.sessions || [];
  const elections = electionsData?.elections || [];

  // Progress tracking
  const {
    progress: allProgress,
    startScraper,
    completeScraper,
    clearProgress,
    addError,
    updateProgress,
    setArtifactMetadata,
  } = useScraperProgressStore();
  const progress = allProgress[code] || null;

  // Re-render on elapsed time update
  const [, setTick] = useState(0);
  useEffect(() => {
    if (!progress?.isRunning) return;
    const timer = setInterval(() => setTick((t) => t + 1), 1000);
    return () => clearInterval(timer);
  }, [progress?.isRunning]);

  const runMutation = useMutation({
    mutationFn: async () => {
      startScraper(code, { cacheMode, verbose });

      try {
        const res = await scrapersAPI.runStateScraperStream(code, { cacheMode, verbose });
        const reader = res.body.getReader();
        const decoder = new TextDecoder();
        let buffer = '';
        let finalResult = { ok: true };

        while (true) {
          const { done, value } = await reader.read();
          if (done) break;

          buffer += decoder.decode(value, { stream: true });
          const blocks = buffer.split('\n\n');
          buffer = blocks.pop() ?? '';

          for (const block of blocks) {
            const lines = block.split('\n').filter((l) => l.trim());
            let type = '';
            let data = null;

            for (const line of lines) {
              if (line.startsWith('event: ')) type = line.slice(7).trim();
              if (line.startsWith('data: ')) {
                try {
                  data = JSON.parse(line.slice(6).trim());
                } catch {
                  data = line.slice(6).trim();
                }
              }
            }

            if (type === 'progress' && data) {
              updateProgress(code, {
                message: data.message,
                electionCount: data.created || 0,
                pagesVisited: data.updated || 0,
                electionsParsed: data.electionsParsed,
                datesFound: data.datesFound,
                scheduleWindowsFound: data.scheduleWindowsFound,
              });
              // Capture artifact metadata if present
              if (data.artifactMetadata) {
                setArtifactMetadata(code, data.artifactMetadata);
              }
            } else if (type === 'error' && data) {
              addError(code, {
                message: data.message,
                code: data.statusCode,
                url: data.url,
              });
            } else if (type === 'complete') {
              if (!data.ok) {
                if (data.errorData) {
                  addError(code, {
                    message: data.errorData.message,
                    code: data.errorData.statusCode,
                    url: data.errorData.url,
                  });
                } else {
                  addError(code, data.error || 'Unknown error');
                }
              }

              const resultData = data?.result || data;
              let debugMessage = '';
              if (resultData.electionsParsed !== undefined && resultData.datesFound !== undefined) {
                debugMessage =
                  `Elections: ${resultData.electionsParsed}, ` +
                  `Dates: ${resultData.datesFound}, ` +
                  `Election windows: ${resultData.scheduleWindowsFound ?? 0}.${
                    resultData.verbose ? `\n\n${resultData.verbose}` : ''
                  }`;
              }

              // Capture artifact metadata from final result
              if (resultData.artifactMetadata) {
                setArtifactMetadata(code, resultData.artifactMetadata);
              }

              finalResult = {
                ok: Boolean(data?.ok),
                message:
                  data?.errorData?.message || data?.error || data?.message || 'Scraper completed',
                status: data?.ok ? 'completed' : 'error',
                debugMessage,
                scheduleWindowsFound: resultData.scheduleWindowsFound ?? 0,
              };

              return finalResult;
            }
          }
        }

        return finalResult;
      } catch (err) {
        addError(code, err.message);
        throw err;
      }
    },

    onSuccess: (result) => {
      completeScraper(code, result);
      queryClient.invalidateQueries({ queryKey: ['scrapers-states'] });
      queryClient.invalidateQueries({ queryKey: ['scraper-sessions', code] });
      queryClient.invalidateQueries({
        queryKey: ['elections-by-datasource', `state_${code.toLowerCase()}`],
      });

      // Successful results now persist until manually dismissed
    },

    onError: (err) => {
      completeScraper(code, {
        ok: false,
        status: 'error',
        message: err?.message || 'Scraper failed',
      });
    },
  });

  const resetStatusMutation = useMutation({
    mutationFn: () => scrapersAPI.resetStaleScraperStatus(code),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['scrapers-states'] });
    },
  });

  const breadcrumbs = [
    { label: 'Admin', url: '/admin/scrapers' },
    { label: 'Scrapers', url: '/admin/scrapers' },
    { label: code },
  ];

  const hero = scraper ? (
    <div className="space-y-4">
      <div className="flex items-start justify-between">
        <div>
          <h1 className="text-2xl font-bold text-neutral-900 mb-1">{scraper.name}</h1>
          <p className="text-sm text-neutral-500">State code: {scraper.state}</p>
        </div>
        {progress?.isRunning ? (
          <div className="flex gap-2">
            <button
              disabled
              className="px-4 py-2 text-sm font-medium bg-blue-600 text-white rounded-lg opacity-75 whitespace-nowrap"
            >
              ⏳ {progress?.message || 'Running...'}
            </button>
            <button
              onClick={() => {
                scrapersAPI.stopStateScraper(code);
                runMutation.reset();
                completeScraper(code);
              }}
              className="px-4 py-2 text-sm font-medium bg-red-600 text-white rounded-lg hover:bg-red-700 transition-colors whitespace-nowrap"
            >
              Stop
            </button>
          </div>
        ) : (
          <div className="flex flex-col gap-2 items-end">
            <button
              onClick={() => runMutation.mutate()}
              disabled={runMutation.isPending}
              className="px-4 py-2 text-sm font-medium bg-primary-600 text-white rounded-lg hover:bg-primary-700 disabled:opacity-50 transition-colors whitespace-nowrap"
            >
              Run Scraper
            </button>
            <div className="border border-neutral-200 rounded-lg p-3 bg-neutral-50 space-y-2 w-full">
              <div>
                <label className="text-xs font-medium text-neutral-700 block mb-2">
                  Cache Mode
                </label>
                <select
                  value={cacheMode}
                  onChange={(e) => setCacheMode(e.target.value)}
                  disabled={progress?.isRunning}
                  className="w-full px-2 py-1.5 text-xs border border-neutral-300 rounded bg-white text-neutral-900 disabled:opacity-50"
                >
                  <option value="off">Live (always fetch)</option>
                  <option value="write">Fetch + cache</option>
                  <option value="read">Cache only</option>
                  <option value="refresh">Refresh cache</option>
                </select>
                <p className="text-xs text-neutral-500 mt-1">
                  {cacheMode === 'off' && 'Fetch from source, do not rely on cache'}
                  {cacheMode === 'write' && 'Fetch fresh and save artifact'}
                  {cacheMode === 'read' && 'Parse saved artifact only'}
                  {cacheMode === 'refresh' && 'Fetch fresh and overwrite saved artifact'}
                </p>
              </div>
              <div className="flex items-center gap-2">
                <input
                  type="checkbox"
                  id="verbose-toggle"
                  checked={verbose}
                  onChange={(e) => setVerbose(e.target.checked)}
                  disabled={progress?.isRunning}
                  className="w-4 h-4 border-neutral-300 rounded cursor-pointer disabled:opacity-50"
                />
                <label
                  htmlFor="verbose-toggle"
                  className="text-xs font-medium text-neutral-700 cursor-pointer"
                >
                  Verbose output
                </label>
              </div>
            </div>
          </div>
        )}
      </div>
      <div className="grid grid-cols-2 sm:grid-cols-4 gap-4 pt-3">
        <div>
          <p className="text-xs font-medium text-neutral-700 mb-1">Scraper Key</p>
          <p className="text-sm font-mono text-neutral-800">{scraper.key}</p>
        </div>
        <div>
          <p className="text-xs font-medium text-neutral-700 mb-1">Type</p>
          <p className="text-sm text-neutral-800">{scraper.isFallback ? 'Fallback' : 'Live'}</p>
        </div>
        <div>
          <p className="text-xs font-medium text-neutral-700 mb-1">Last Run</p>
          <p className="text-sm text-neutral-800">
            {scraper.lastRunAt ? fmt(scraper.lastRunAt) : 'Never'}
          </p>
        </div>
        <div>
          <p className="text-xs font-medium text-neutral-700 mb-1">Fetch Strategy</p>
          <span
            className={`inline-flex items-center rounded-full border px-2 py-0.5 text-xs font-medium ${
              FETCH_STRATEGY_STYLES[scraper.fetchStrategy?.key] ??
              'bg-neutral-50 text-neutral-600 border-neutral-200'
            }`}
          >
            {scraper.fetchStrategy?.label || '—'}
          </span>
        </div>
      </div>
      {progress && (
        <div className="pt-2 border-t border-neutral-100 space-y-2">
          {progress?.isRunning && <p className="text-xs text-neutral-600">{progress.message}</p>}
          {progress?.isRunning && (
            <div className="grid grid-cols-3 gap-2 text-xs">
              <div className="bg-neutral-50 rounded p-2">
                <p className="text-neutral-500">Elapsed</p>
                <p className="font-mono text-neutral-800">{elapsed(progress.startTime)}</p>
              </div>
              <div className="bg-neutral-50 rounded p-2">
                <p className="text-neutral-500">Elections Found</p>
                <p className="font-mono text-neutral-800">{progress.electionCount || 0}</p>
              </div>
              <div className="bg-neutral-50 rounded p-2">
                <p className="text-neutral-500">Pages Visited</p>
                <p className="font-mono text-neutral-800">{progress.pagesVisited || 0}</p>
              </div>
            </div>
          )}
          {progress.errors?.length > 0 && (
            <div className="bg-red-50 border border-red-200 rounded p-2 text-xs space-y-2">
              <p className="font-medium text-red-900">Errors ({progress.errors.length}):</p>
              <div className="space-y-2 max-h-48 overflow-y-auto">
                {progress.errors.map((err, i) => (
                  <div key={i} className="bg-white border border-red-100 rounded p-2 text-red-800">
                    <div className="font-mono text-xs">
                      {typeof err === 'string' ? (
                        <p>{err}</p>
                      ) : (
                        <>
                          <p className="font-medium text-red-900">{err.message}</p>
                          {err.code && <p className="text-red-700">Code: {err.code}</p>}
                          {err.url && <p className="text-red-700 break-all">URL: {err.url}</p>}
                          {err.timestamp && (
                            <p className="text-red-600 text-xs">
                              {new Date(err.timestamp).toLocaleTimeString()}
                            </p>
                          )}
                        </>
                      )}
                    </div>
                  </div>
                ))}
              </div>
            </div>
          )}
        </div>
      )}
      {progress && !progress.isRunning ? (
        <div className="pt-2 border-t border-neutral-100 space-y-2">
          <p className="text-xs text-neutral-600">
            Last local run:{' '}
            <span
              className={`font-medium ${
                progress.status === 'error'
                  ? 'text-red-600'
                  : progress.status === 'completed'
                    ? 'text-emerald-600'
                    : 'text-neutral-500'
              }`}
            >
              {progress.status || 'finished'}
            </span>
          </p>

          {progress.terminalMessage && (
            <div
              className={`rounded p-2 text-xs border ${
                progress.status === 'error'
                  ? 'bg-red-50 border-red-200 text-red-900'
                  : 'bg-emerald-50 border-emerald-200 text-emerald-900'
              }`}
            >
              <p className="font-medium">
                {progress.status === 'error' ? 'Last error' : 'Last result'}
              </p>
              <p className="mt-1">{progress.terminalMessage}</p>
              {progress.scheduleWindowsFound !== null && progress.status === 'completed' && (
                <p className="mt-2 text-xs font-medium text-emerald-900">
                  Election windows found: {progress.scheduleWindowsFound}
                </p>
              )}
              {progress.debugMessage && (
                <p className="mt-2 font-mono text-xs whitespace-pre-wrap">
                  {progress.debugMessage}
                </p>
              )}
              {progress.lastError?.url && (
                <p className="mt-1 break-all text-red-700">URL: {progress.lastError.url}</p>
              )}
              {progress.endTime && (
                <p className="mt-1 text-neutral-500">
                  {new Date(progress.endTime).toLocaleString()}
                </p>
              )}
            </div>
          )}

          {progress.artifactMetadata && (
            <div className="border border-blue-200 bg-blue-50 rounded p-3 space-y-2">
              <p className="font-medium text-xs text-blue-900">
                {progress.artifactMetadata.fromCache ? '📦 Loaded from cache' : '🌐 Fetched fresh'}
              </p>
              <div className="space-y-1 text-xs text-blue-800">
                {progress.artifactMetadata.cacheMode && (
                  <p>
                    <span className="font-medium">Cache mode:</span>{' '}
                    {progress.artifactMetadata.cacheMode}
                  </p>
                )}
                {progress.artifactMetadata.cachePath && (
                  <p className="break-all">
                    <span className="font-medium">Path:</span> {progress.artifactMetadata.cachePath}
                  </p>
                )}
                {progress.artifactMetadata.cachePath && (
                  <p>
                    <span className="font-medium">Cached webpage:</span>{' '}
                    <a
                      href={scrapersAPI.getCachedWebpageUrl(code)}
                      target="_blank"
                      rel="noopener noreferrer"
                      className="text-blue-600 hover:underline"
                    >
                      Open cached page
                    </a>
                  </p>
                )}
                {progress.artifactMetadata.sourceUrl && (
                  <p className="break-all">
                    <span className="font-medium">Source:</span>{' '}
                    <a
                      href={progress.artifactMetadata.sourceUrl}
                      target="_blank"
                      rel="noopener noreferrer"
                      className="text-blue-600 hover:underline"
                    >
                      {new URL(progress.artifactMetadata.sourceUrl).hostname}
                    </a>
                  </p>
                )}
                {progress.artifactMetadata.fetchedAt && (
                  <p>
                    <span className="font-medium">Fetched:</span>{' '}
                    {new Date(progress.artifactMetadata.fetchedAt).toLocaleString()}
                  </p>
                )}
              </div>
            </div>
          )}

          {progress.status === 'error' && (
            <button
              onClick={() => clearProgress(code)}
              className="px-3 py-1.5 text-xs font-medium text-red-700 bg-red-50 border border-red-200 rounded hover:bg-red-100 transition-colors"
            >
              Dismiss local error
            </button>
          )}

          {progress.status === 'completed' && (
            <button
              onClick={() => clearProgress(code)}
              className="px-3 py-1.5 text-xs font-medium text-emerald-700 bg-emerald-50 border border-emerald-200 rounded hover:bg-emerald-100 transition-colors"
            >
              Dismiss local result
            </button>
          )}
        </div>
      ) : (
        <div className="pt-2 border-t border-neutral-100">
          {scraper.lastRunStatus === 'running' ? (
            <div className="bg-amber-50 border border-amber-200 rounded p-2 space-y-2">
              <p className="text-xs text-amber-900 font-medium">⚠️ Stale run status detected</p>
              <p className="text-xs text-amber-800">
                The database shows this scraper was running, but it appears to have stopped. This
                can happen if a previous run was interrupted or the server crashed.
              </p>
              <div className="flex gap-2">
                <button
                  onClick={() => resetStatusMutation.mutate()}
                  disabled={resetStatusMutation.isPending}
                  className="px-3 py-1.5 text-xs font-medium text-amber-700 bg-amber-100 border border-amber-300 rounded hover:bg-amber-200 disabled:opacity-50 transition-colors"
                >
                  {resetStatusMutation.isPending ? 'Resetting...' : 'Mark as error'}
                </button>
                <button
                  onClick={() => queryClient.invalidateQueries({ queryKey: ['scrapers-states'] })}
                  className="px-3 py-1.5 text-xs font-medium text-amber-600 border border-amber-300 rounded hover:bg-amber-50 transition-colors"
                >
                  Refresh from server
                </button>
              </div>
            </div>
          ) : (
            <p className="text-xs text-neutral-600">
              Last run:{' '}
              <span
                className={`font-medium ${STATUS_STYLES[scraper.lastRunStatus] || 'text-neutral-500'}`}
              >
                {scraper.lastRunStatus || 'Never run'}
              </span>
            </p>
          )}
        </div>
      )}
    </div>
  ) : null;

  const sections = [
    {
      title: 'URLs',
      content: (() => {
        const scraperCalendarUrl = urlsData?.scraperCalendarUrl ?? scraper?.calendarUrl ?? null;
        const websiteUrl = urlsData?.websiteUrl ?? null;
        const calendarUrl = urlsData?.calendarUrl ?? null;

        if (urlEdit) {
          return (
            <div className="space-y-3">
              <div>
                <label className="block text-xs font-medium text-neutral-700 mb-1">
                  State Site URL
                </label>
                <input
                  type="url"
                  className="w-full text-sm border border-neutral-300 rounded px-3 py-1.5 font-mono"
                  value={urlEdit.websiteUrl}
                  onChange={(e) => setUrlEdit((prev) => ({ ...prev, websiteUrl: e.target.value }))}
                  placeholder="https://..."
                />
              </div>
              <div>
                <label className="block text-xs font-medium text-neutral-700 mb-1">
                  Calendar URL
                </label>
                <input
                  type="url"
                  className="w-full text-sm border border-neutral-300 rounded px-3 py-1.5 font-mono"
                  value={urlEdit.calendarUrl}
                  onChange={(e) => setUrlEdit((prev) => ({ ...prev, calendarUrl: e.target.value }))}
                  placeholder="https://..."
                />
              </div>
              <div className="flex gap-2 pt-1">
                <button
                  onClick={() =>
                    urlMutation.mutate({
                      websiteUrl: urlEdit.websiteUrl,
                      calendarUrl: urlEdit.calendarUrl,
                    })
                  }
                  disabled={urlMutation.isPending}
                  className="px-3 py-1.5 text-sm font-medium bg-primary-600 text-white rounded hover:bg-primary-700 disabled:opacity-50 transition-colors"
                >
                  {urlMutation.isPending ? 'Saving...' : 'Save'}
                </button>
                <button
                  onClick={() => setUrlEdit(null)}
                  className="px-3 py-1.5 text-sm font-medium text-neutral-600 border border-neutral-300 rounded hover:bg-neutral-50 transition-colors"
                >
                  Cancel
                </button>
              </div>
              {urlMutation.isError && (
                <p className="text-xs text-red-600">
                  {urlMutation.error?.message || 'Save failed'}
                </p>
              )}
            </div>
          );
        }

        const effectiveCalendarUrl = calendarUrl || scraperCalendarUrl;

        return (
          <div className="space-y-3">
            <div className="grid grid-cols-1 gap-3 text-sm">
              <div>
                <p className="text-xs font-medium text-neutral-500 mb-0.5">State Site URL</p>
                {websiteUrl ? (
                  <a
                    href={websiteUrl}
                    target="_blank"
                    rel="noopener noreferrer"
                    className="text-primary-600 hover:underline font-mono text-xs break-all"
                  >
                    {websiteUrl}
                  </a>
                ) : (
                  <p className="text-neutral-400 text-xs italic">Not set</p>
                )}
              </div>
              <div>
                <p className="text-xs font-medium text-neutral-500 mb-0.5">
                  Calendar URL{calendarUrl ? '' : ' (scraper default)'}
                </p>
                {effectiveCalendarUrl ? (
                  <a
                    href={effectiveCalendarUrl}
                    target="_blank"
                    rel="noopener noreferrer"
                    className={`hover:underline font-mono text-xs break-all ${calendarUrl ? 'text-primary-600' : 'text-neutral-400'}`}
                  >
                    {effectiveCalendarUrl}
                  </a>
                ) : (
                  <p className="text-neutral-400 text-xs italic">Not set</p>
                )}
              </div>
              <div>
                <p className="text-xs font-medium text-neutral-500 mb-0.5">Cached Webpage</p>
                <a
                  href={scrapersAPI.getCachedWebpageUrl(code)}
                  target="_blank"
                  rel="noopener noreferrer"
                  className="text-primary-600 hover:underline font-mono text-xs break-all"
                >
                  Open latest cached source page
                </a>
              </div>
            </div>
            <button
              onClick={() =>
                setUrlEdit({
                  websiteUrl: websiteUrl ?? '',
                  calendarUrl: effectiveCalendarUrl ?? '',
                })
              }
              className="px-3 py-1.5 text-sm font-medium text-neutral-600 border border-neutral-300 rounded hover:bg-neutral-50 transition-colors"
            >
              Edit URLs
            </button>
          </div>
        );
      })(),
    },
    {
      title: 'Scraped Elections',
      content: (
        <div>
          {electionsLoading ? (
            <p className="text-sm text-neutral-500">Loading elections...</p>
          ) : elections.length === 0 ? (
            <p className="text-sm text-neutral-500">No elections scraped yet for {code}.</p>
          ) : (
            <div className="overflow-x-auto">
              <table className="w-full text-xs">
                <thead>
                  <tr className="text-neutral-400 border-b border-neutral-200">
                    <th className="text-left py-2 font-medium">Election Name</th>
                    <th className="text-left py-2 font-medium">Date</th>
                    <th className="text-left py-2 font-medium">Type</th>
                    <th className="text-left py-2 font-medium">County</th>
                    <th className="text-left py-2 font-medium">Source</th>
                    <th className="text-left py-2 font-medium">Last Checked</th>
                    <th className="text-left py-2 font-medium">Last Changed</th>
                  </tr>
                </thead>
                <tbody className="divide-y divide-neutral-100">
                  {elections.map((election) => (
                    <tr key={election.id} className="hover:bg-neutral-50">
                      <td className="py-2 text-neutral-700">
                        <Link
                          to={`/elections/${election.id}`}
                          className="hover:text-blue-600 hover:underline"
                        >
                          {election.name}
                        </Link>
                      </td>
                      <td className="py-2 text-neutral-600 whitespace-nowrap">
                        {election.electionDay}
                      </td>
                      <td className="py-2 text-neutral-600">{election.electionType}</td>
                      <td className="py-2 text-neutral-600">{election.county || '—'}</td>
                      <td className="py-2 text-neutral-600">{election.dataSource}</td>
                      <td className="py-2 text-neutral-500 whitespace-nowrap">
                        {election.lastSyncedAt
                          ? new Date(election.lastSyncedAt).toLocaleString([], {
                              month: 'short',
                              day: 'numeric',
                              year: '2-digit',
                              hour: '2-digit',
                              minute: '2-digit',
                            })
                          : '—'}
                      </td>
                      <td className="py-2 text-neutral-600 whitespace-nowrap">
                        {election.lastChangedAt
                          ? new Date(election.lastChangedAt).toLocaleString([], {
                              month: 'short',
                              day: 'numeric',
                              year: '2-digit',
                              hour: '2-digit',
                              minute: '2-digit',
                            })
                          : '—'}
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}
        </div>
      ),
    },
    {
      title: 'Website Discovery',
      content: (
        <div className="space-y-4">
          <div className="flex items-center justify-between gap-4">
            <div className="flex-1">
              {isDiscoveryRunning ? (
                <div className="space-y-3">
                  <p className="text-sm font-medium text-neutral-700">
                    {liveStats?.status || 'Discovery in progress...'}
                  </p>
                  <div className="grid grid-cols-3 gap-4 text-xs">
                    <div>
                      <p className="text-neutral-500">Pages Visited</p>
                      <p className="font-medium text-neutral-800">{liveStats?.pagesVisited || 0}</p>
                    </div>
                    <div>
                      <p className="text-neutral-500">With Elections</p>
                      <p className="font-medium text-neutral-800">
                        {liveStats?.pagesWithElections || 0}
                      </p>
                    </div>
                    <div>
                      <p className="text-neutral-500">URLs Crawled</p>
                      <p className="font-medium text-neutral-800">{crawledUrls?.length ?? 0}</p>
                    </div>
                  </div>
                  {liveStats?.issues && liveStats.issues.length > 0 && (
                    <div className="bg-amber-50 border border-amber-200 rounded p-2 text-xs">
                      <p className="font-medium text-amber-900 mb-1">Issues encountered:</p>
                      <ul className="space-y-1">
                        {liveStats.issues.map((issue, i) => (
                          <li key={i} className="text-amber-800">
                            • {issue}
                          </li>
                        ))}
                      </ul>
                    </div>
                  )}
                </div>
              ) : scraper && !scraper.isFallback ? (
                <p className="text-sm text-neutral-500">
                  This state has a dedicated scraper — use <strong>Run Scraper</strong> above to
                  collect elections.
                </p>
              ) : liveStats ? (
                <div>
                  <p className="text-sm font-medium text-neutral-700 mb-2">{liveStats.status}</p>
                  {liveStats?.issues && liveStats.issues.length > 0 && (
                    <div className="bg-red-50 border border-red-200 rounded p-2 text-xs">
                      <p className="font-medium text-red-900 mb-1">Issues encountered:</p>
                      <ul className="space-y-1">
                        {liveStats.issues.map((issue, i) => (
                          <li key={i} className="text-red-800">
                            • {issue}
                          </li>
                        ))}
                      </ul>
                    </div>
                  )}
                </div>
              ) : (
                <p className="text-sm text-neutral-500">No discovery run yet.</p>
              )}
            </div>
            {isDiscoveryRunning ? (
              <button
                onClick={() => stopDiscovery(code)}
                className="px-4 py-2 text-sm font-medium bg-red-600 text-white rounded-lg hover:bg-red-700 transition-colors whitespace-nowrap"
              >
                Stop Discovery
              </button>
            ) : (
              <button
                onClick={() =>
                  startDiscovery(code, () =>
                    queryClient.invalidateQueries({ queryKey: ['scraper-sessions', code] })
                  )
                }
                className="px-4 py-2 text-sm font-medium bg-amber-600 text-white rounded-lg hover:bg-amber-700 transition-colors whitespace-nowrap"
              >
                Run Discovery
              </button>
            )}
          </div>

          {isDiscoveryRunning && (crawledUrls?.length ?? 0) > 0 && (
            <div className="border-t border-neutral-200 pt-4">
              <h3 className="text-sm font-medium text-neutral-700 mb-3">
                Sitemap ({crawledUrls?.length ?? 0} URLs)
              </h3>
              <UrlTree urls={crawledUrls ?? []} baseUrl={scraper?.calendarUrl} />
            </div>
          )}
        </div>
      ),
    },
    {
      title: 'Discovery Sessions',
      content: (
        <div>
          {sessions.length === 0 ? (
            <p className="text-sm text-neutral-500">No discovery sessions yet for {code}.</p>
          ) : (
            <div className="overflow-x-auto">
              <table className="w-full text-xs">
                <thead>
                  <tr className="text-neutral-400 border-b border-neutral-200">
                    <th className="text-left py-2 font-medium">Started</th>
                    <th className="text-left py-2 font-medium">Status</th>
                    <th className="text-left py-2 font-medium">Pages Crawled</th>
                    <th className="text-left py-2 font-medium">Elections Found</th>
                    <th className="text-left py-2 font-medium">Dates Found</th>
                    <th className="text-left py-2 font-medium">Duration</th>
                    <th className="text-left py-2 font-medium">Error</th>
                  </tr>
                </thead>
                <tbody className="divide-y divide-neutral-100">
                  {sessions.map((session) => (
                    <tr key={session.id} className="hover:bg-neutral-50">
                      <td className="py-2 text-neutral-700 whitespace-nowrap">
                        {fmt(session.startedAt)}
                      </td>
                      <td className="py-2">
                        <span
                          className={`font-medium ${STATUS_STYLES[session.status] || 'text-neutral-500'}`}
                        >
                          {session.status}
                        </span>
                      </td>
                      <td className="py-2 text-neutral-600">{session.pagesVisited ?? 0}</td>
                      <td className="py-2 text-neutral-600">{session.pagesWithElections ?? 0}</td>
                      <td className="py-2 text-neutral-600">{session.patterns?.datesFound ?? 0}</td>
                      <td className="py-2 text-neutral-600 whitespace-nowrap">
                        {ms(session.durationMs)}
                      </td>
                      <td className="py-2 text-red-600 truncate max-w-xs">
                        {session.errorMessage || '—'}
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}
        </div>
      ),
    },
  ];

  return (
    <DetailPageLayout
      title={`${code} Scraper`}
      breadcrumbs={breadcrumbs}
      hero={hero}
      sections={sections}
      isLoading={isLoading && !scraper}
      error={error}
      containerClassName="max-w-4xl"
    />
  );
}
